import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../../config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [isRoot, setIsRoot] = useState(false);
  const [impersonation, setImpersonation] = useState({ active: false, root_user: null, target_user: null });
  const [legal, setLegal] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (!res.ok) {
        setUser(null);
        setDisplayName(null);
        setNeedsEmail(false);
        setIsRoot(false);
        setImpersonation({ active: false, root_user: null, target_user: null });
        setLegal(null);
        return;
      }
      const data = await res.json();
      setUser(data.user || null);
      setDisplayName(data.displayName || null);
      setNeedsEmail(Boolean(data.needs_email));
      setIsRoot(Boolean(data.is_root));
      setImpersonation(
        data.impersonation && typeof data.impersonation === 'object'
          ? data.impersonation
          : { active: false, root_user: null, target_user: null }
      );
      setLegal(data.legal && typeof data.legal === 'object' ? data.legal : null);
    } catch {
      setUser(null);
      setDisplayName(null);
      setNeedsEmail(false);
      setIsRoot(false);
      setImpersonation({ active: false, root_user: null, target_user: null });
      setLegal(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } finally {
      setUser(null);
      setDisplayName(null);
      setNeedsEmail(false);
      setIsRoot(false);
      setImpersonation({ active: false, root_user: null, target_user: null });
      setLegal(null);
    }
  };

  const searchUsers = async (q) => {
    const query = String(q || '').trim();
    if (!query || query.length < 2) return [];
    const res = await fetch(`${API_BASE}/auth/admin/users/search?q=${encodeURIComponent(query)}`, { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data?.users) ? data.users : [];
  };

  const impersonate = async (userId) => {
    const id = String(userId || '').trim();
    if (!id) throw new Error('Missing user id');
    const res = await fetch(`${API_BASE}/auth/admin/impersonate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: id })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || 'Impersonation failed');
    }
    await refresh();
  };

  const resetImpersonation = async () => {
    const res = await fetch(`${API_BASE}/auth/admin/impersonate/reset`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || 'Reset failed');
    }
    await refresh();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      loading,
      user,
      displayName,
      needsEmail,
      isRoot,
      impersonation,
      legal,
      refresh,
      logout,
      searchUsers,
      impersonate,
      resetImpersonation
    }),
    [loading, user, displayName, needsEmail, isRoot, impersonation, legal]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}


